import { expectArray, expectBoolean, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseEnrollmentStatus, parseEnrollmentType } from "./admin-roster";
import { parseRelationType } from "./guardian";
import { parseStudentAbsenceNoticeStatus } from "./student-absence";
export const FAMILY_SCHEDULE_ITEM_TYPES = ["occurrence", "event"];
export const FAMILY_SCHEDULE_VIEW_MODES = ["siblingMergedChronological"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
export function parseFamilyScheduleItemType(value, path = "scheduleItemType") {
    return parseStringEnum(value, FAMILY_SCHEDULE_ITEM_TYPES, path);
}
export function parseFamilyScheduleViewMode(value, path = "scheduleViewMode") {
    return parseStringEnum(value, FAMILY_SCHEDULE_VIEW_MODES, path);
}
export function parseFamilyScheduleDisplayFlags(value, path = "familyScheduleDisplayFlags") {
    const record = expectRecord(value, path);
    return {
        isCancelled: expectBoolean(record.isCancelled, `${path}.isCancelled`),
        isChanged: expectBoolean(record.isChanged, `${path}.isChanged`),
        isRescheduled: expectBoolean(record.isRescheduled, `${path}.isRescheduled`),
        isSubstitute: expectBoolean(record.isSubstitute, `${path}.isSubstitute`),
        hasVideoReplacement: expectBoolean(record.hasVideoReplacement, `${path}.hasVideoReplacement`),
        isStudioTbd: expectBoolean(record.isStudioTbd, `${path}.isStudioTbd`),
        isTeacherTbd: expectBoolean(record.isTeacherTbd, `${path}.isTeacherTbd`),
        hasPublicNote: expectBoolean(record.hasPublicNote, `${path}.hasPublicNote`),
        showStudio: expectBoolean(record.showStudio, `${path}.showStudio`),
        showTeacherNames: expectBoolean(record.showTeacherNames, `${path}.showTeacherNames`)
    };
}
export function parseStudentSummary(value, path = "studentSummary") {
    const record = expectRecord(value, path);
    const primaryRelationType = parseRelationType(record.primaryRelationType, `${path}.primaryRelationType`);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        birthDate: expectOptionalString(record.birthDate, `${path}.birthDate`),
        status: expectString(record.status, `${path}.status`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        primaryRelationType,
        relationTypes: record.relationTypes === undefined
            ? [primaryRelationType]
            : expectArray(record.relationTypes, parseRelationType, `${path}.relationTypes`),
        nextScheduledAt: expectOptionalString(record.nextScheduledAt, `${path}.nextScheduledAt`),
        upcomingCount: expectNumber(record.upcomingCount, `${path}.upcomingCount`),
        profilePhoto: record.profilePhoto === undefined || record.profilePhoto === null
            ? null
            : parseStudentProfilePhoto(record.profilePhoto, `${path}.profilePhoto`)
    };
}
export function parseStudentProfilePhoto(value, path = "studentProfilePhoto") {
    const record = expectRecord(value, path);
    return {
        downloadUrl: expectString(record.downloadUrl, `${path}.downloadUrl`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseFamilyScheduleStudentAbsence(value, path = "familyScheduleStudentAbsence") {
    const record = expectRecord(value, path);
    const absenceStatus = record.absenceStatus === "none" || record.absenceStatus === null || record.absenceStatus === undefined
        ? "none"
        : parseStudentAbsenceNoticeStatus(record.absenceStatus, `${path}.absenceStatus`);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        studentDisplayName: expectString(record.studentDisplayName, `${path}.studentDisplayName`),
        absenceStatus,
        isAbsent: record.isAbsent === undefined ? false : expectBoolean(record.isAbsent, `${path}.isAbsent`)
    };
}
export function parseFamilyScheduleItem(value, path = "familyScheduleItem") {
    const record = expectRecord(value, path);
    return {
        itemId: expectString(record.itemId, `${path}.itemId`),
        itemType: parseFamilyScheduleItemType(record.itemType, `${path}.itemType`),
        title: expectString(record.title, `${path}.title`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        locationLabel: expectOptionalString(record.locationLabel, `${path}.locationLabel`),
        teacherNameSnapshots: expectArray(record.teacherNameSnapshots, expectString, `${path}.teacherNameSnapshots`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        studentIds: expectArray(record.studentIds, expectString, `${path}.studentIds`),
        studentDisplayNames: expectArray(record.studentDisplayNames, expectString, `${path}.studentDisplayNames`),
        studentAbsences: Array.isArray(record.studentAbsences)
            ? expectArray(record.studentAbsences, parseFamilyScheduleStudentAbsence, `${path}.studentAbsences`)
            : [],
        canReportAbsence: record.canReportAbsence === undefined ? false : expectBoolean(record.canReportAbsence, `${path}.canReportAbsence`),
        displayFlags: parseFamilyScheduleDisplayFlags(record.displayFlags, `${path}.displayFlags`)
    };
}
export function parseFamilyScheduleMonthGroup(value, path = "familyScheduleMonthGroup") {
    const record = expectRecord(value, path);
    return {
        yearMonth: expectString(record.yearMonth, `${path}.yearMonth`),
        label: expectString(record.label, `${path}.label`),
        items: expectArray(record.items, parseFamilyScheduleItem, `${path}.items`)
    };
}
export function parseFamilyScheduleResponse(value, path = "familyScheduleResponse") {
    const record = expectRecord(value, path);
    return {
        rangeStart: expectString(record.rangeStart, `${path}.rangeStart`),
        rangeEnd: expectString(record.rangeEnd, `${path}.rangeEnd`),
        viewMode: parseFamilyScheduleViewMode(record.viewMode, `${path}.viewMode`),
        monthGroups: expectArray(record.monthGroups, parseFamilyScheduleMonthGroup, `${path}.monthGroups`)
    };
}
export function parseFamilyDashboardResponse(value, path = "familyDashboardResponse") {
    const record = expectRecord(value, path);
    return {
        studentSummaries: expectArray(record.studentSummaries, parseStudentSummary, `${path}.studentSummaries`),
        upcomingItems: expectArray(record.upcomingItems, parseFamilyScheduleItem, `${path}.upcomingItems`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseFamilyStudentsResponse(value, path = "familyStudentsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseStudentSummary, `${path}.items`)
    };
}
export function parseFamilyStudentEnrollmentSummary(value, path = "familyStudentEnrollmentSummary") {
    const record = expectRecord(value, path);
    return {
        enrollmentId: expectString(record.enrollmentId, `${path}.enrollmentId`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        enrollmentType: parseEnrollmentType(record.enrollmentType, `${path}.enrollmentType`),
        status: parseEnrollmentStatus(record.status, `${path}.status`),
        joinDate: expectString(record.joinDate, `${path}.joinDate`),
        leaveDate: expectOptionalString(record.leaveDate, `${path}.leaveDate`)
    };
}
export function parseFamilyStudentDetail(value, path = "familyStudentDetail") {
    const record = expectRecord(value, path);
    return {
        student: parseStudentSummary(record.student, `${path}.student`),
        enrollments: expectArray(record.enrollments, parseFamilyStudentEnrollmentSummary, `${path}.enrollments`),
        upcomingItems: expectArray(record.upcomingItems, parseFamilyScheduleItem, `${path}.upcomingItems`)
    };
}
